import React, { useState, useEffect } from 'react';
import { List, Tag, Space, Button, Tabs } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import axios from 'axios';

const PAGE_SIZE = 10;

const PrintHistory = ({
  sessionHistory,
  handlePrint,
  setSelectedOrder,
  updatePreview
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [unprintedOrders, setUnprintedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('history');

  const fetchUnprintedOrders = async () => {
    if (activeTab !== 'unprinted') return;
    
    setLoading(true);
    try {
      const response = await axios.get('/api/orders/unprinted/', {
        params: { days: 3 }
      });
      setUnprintedOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch unprinted orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnprintedOrders();
  }, [activeTab]);

  // Calculate paginated data
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedHistory = sessionHistory.slice(startIndex, startIndex + PAGE_SIZE);

  const handleItemClick = async (order) => {
    // First just show basic order info
    setSelectedOrder({
      ...order,
      loading: true
    });
    
    try {
      // Then load full order details
      const response = await fetch(`/api/orders/${order.id}/`);
      const fullOrder = await response.json();
      
      setSelectedOrder(fullOrder);
      // Only update preview after we have full order details
      await updatePreview(fullOrder);
    } catch (error) {
      console.error('Failed to load order details:', error);
      // Reset selected order on error
      setSelectedOrder(null);
    }
  };

  const listStyle = {
    width: '100%',
    minWidth: 0, // Prevent list from growing beyond container
    overflow: 'hidden' // Prevent content from causing width changes
  };

  const listItemStyle = {
    width: '100%',
    overflow: 'hidden'
  };

  const spaceStyle = {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const renderHistoryList = () => (
    <List
      style={listStyle}
      loading={loading}
      dataSource={paginatedHistory}
      pagination={{
        current: currentPage,
        pageSize: PAGE_SIZE,
        total: sessionHistory.length,
        onChange: setCurrentPage,
        showSizeChanger: false
      }}
      renderItem={item => (
        <List.Item
          style={{
            ...(item.type === 'success' ? { cursor: 'pointer' } : {}),
            ...listItemStyle
          }}
          onClick={item.type === 'success' ? () => handleItemClick(item.order) : undefined}
        >
          <Space size={2} style={spaceStyle}>
            <Tag color={item.type === 'success' ? 'green' : 'red'}>
              {item.timestamp}
            </Tag>
            {item.type === 'success' ? (
              <>
                <Tag color="blue">订单号: {item.order.order_number}</Tag>
                <Tag color="cyan">快递单号: {item.order.express_number}</Tag>
                <Tag color="blue">
                  收件人: {item.order.recipient_name}
                </Tag>
                <Tag>
                  <span
                    style={{ 
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrint(item.order);
                    }}
                  >
                    <PrinterOutlined /> 打印
                  </span>
                </Tag>
              </>
            ) : (
              <Tag color="red">未找到订单: {item.searchValue}</Tag>
            )}
          </Space>
        </List.Item>
      )}
    />
  );

  const renderUnprintedList = () => (
    <List
      style={listStyle}
      loading={loading}
      dataSource={unprintedOrders}
      pagination={{
        pageSize: PAGE_SIZE,
        showSizeChanger: false
      }}
      renderItem={order => (
        <List.Item
          style={{ cursor: 'pointer', ...listItemStyle }}
          onClick={() => handleItemClick(order)}
        >
          <Space size={2} style={spaceStyle}>
            <Tag color="blue">
              {new Date(order.created_at).toLocaleString()}
            </Tag>
            <Tag color="blue">订单号: {order.order_number}</Tag>
            <Tag color="cyan">快递单号: {order.express_number}</Tag>
            <Tag color="blue">
              收件人: {order.recipient_name}
            </Tag>
            <Tag>
              <span
                style={{ 
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '14px'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrint(order);
                }}
              >
                <PrinterOutlined /> 打印
              </span>
            </Tag>
          </Space>
        </List.Item>
      )}
    />
  );

  const items = [
    {
      key: 'history',
      label: '打印历史记录',
      children: renderHistoryList()
    },
    {
      key: 'unprinted',
      label: '未打印',
      children: renderUnprintedList()
    }
  ];

  return (
    <div style={{ width: '100%' }}>
      <Tabs 
        items={items}
        activeKey={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
};

export default PrintHistory;
